import Enumerable from "linq";
import { IClonable } from "..";
import { AbstractSourceEditor, ISourceReferable } from "../edit";
import { IScaleEditable } from "../presentation";
import { IDocument, IPage } from "./IDocument";
import { IDocumentEditable } from "./IDocumentEditable";
import { IPageEditable } from "./IPageEditable";
import { IEditCommandFactory, IEditCommand, EditCommandSet, RemoveValueOfArrayCommand, MoveValueOfArrayCommand } from "../edit/command";

export abstract class AbstractDocumentEditor<
  TDocument extends IDocument<TPage>,
  TPage extends IPage,
  TPageEditor extends IPageEditable & IEditCommandFactory<TDocument, IEditCommand<TDocument>> & ISourceReferable<TPage> & IClonable<TPageEditor>
> extends AbstractSourceEditor<TDocument> implements IDocumentEditable<TPageEditor>, IScaleEditable {
  constructor(source: TDocument | AbstractDocumentEditor<TDocument, TPage, TPageEditor>) {
    super(source);
    if (source instanceof AbstractDocumentEditor) {
      this._pageIndex = source._pageIndex;
      this._pageEditors = source._pageEditors;
      this._scale = source._scale;
    } else {
      this._pageIndex = 0;
      // _pageEditorsは派生クラスの実装で生成するためここでは生成しない.(派生クラスの初期化はこのタイミングでは終わってないため.)
      this._scale = 1;
    }
  }
  private _pageIndex: number;           // 内部的に管理するのはindex(0～)にする.ページ番号(1～)と間違えないよう注意.
  private _pageEditors: TPageEditor[];
  private _scale: number;

  protected onDone(): void {
    this._pageEditors = this.reconstructPages();
  }

  protected onUndone(): void {
    this._pageEditors = this.reconstructPages();
  }

  getPages(): TPageEditor[] {
    if (!this._pageEditors) {
      this._pageEditors = this.createPages();
    }
    return this._pageEditors;
  }

  protected createPages(): TPageEditor[] {
    return Enumerable.from(this.getOrDefault(s => s.pages, []))
      .select(p => this.createPage(p))
      .toArray();
  }

  protected abstract createPage(source: TPage): TPageEditor;

  protected abstract reconstructPages(): TPageEditor[];

  getCurrentPage(): TPageEditor {
    return this.getPages()[this._pageIndex];    
  }

  getCurrentPageIndex(): number {
    return this._pageIndex;
  }

  updatePages(pages: TPageEditor[]): void {
    // reconstructでも分からないので_pageEditorsもここで更新する.
    for (const page of pages) {
      this._pageEditors = Enumerable.from(this._pageEditors)
      .select(p => p.isReferedSameSource(page) ? page : p)
      .toArray();
    }
    this.apply(pages);
  }

  updatePage(page: TPageEditor): void {
    // reconstructでも分からないので_pageEditorsもここで更新する.
    this._pageEditors = Enumerable.from(this._pageEditors)
      .select(p => p.isReferedSameSource(page) ? page : p)
      .toArray();
    this.apply(page);
  }

  addPage(page: TPageEditor): void {
    // 追加の場合は選択状態を更新したいのでreconstructに任せず自分で更新.
    this.deselectAllPages();
    const added = this.createPage(page.getSource());
    this._pageEditors.push(added);
    this.selectPages(added);
    this.do(this.createAddPageCommand(page));
    Object.freeze(page);
  }

  protected createAddPageCommand(page: TPageEditor): IEditCommand<TDocument> {
    return page.createCommand();
  }

  removePages(pages: TPageEditor[]): void {
    // _pageEditorsの更新はreconstructに任せる.
    const commands = Enumerable.from(pages)
      .select(p => new RemoveValueOfArrayCommand<TDocument, TPage>(s => s.pages, this.toIndex(p)))
      .toArray();
    if (0 < commands.length) {
      this.do(new EditCommandSet(commands));
    }
    this.adjustPageIndex();
  }

  removePage(page: TPageEditor): void {
    // _pageEditorsの更新はreconstructに任せる.
    this.do(new RemoveValueOfArrayCommand(s => s.pages, this.toIndex(page)));
    this.adjustPageIndex();
  }

  private adjustPageIndex(): void {
    const maxPageIndex = this.getPageCount() - 1;
    if (this._pageIndex > maxPageIndex) {
      this._pageIndex = maxPageIndex;
    }
  }

  shiftPages(pages: TPageEditor[], offset: number): void {
    // _pageEditorsの更新はreconstructに任せる.
    const commands = Enumerable.from(pages)
      .where(p => ((this.toIndex(p) + offset) >= 0) && ((this.toIndex(p) + offset) < this._pageEditors.length))
      .select(p => new MoveValueOfArrayCommand<TDocument, TPage>(s => s.pages, this.toIndex(p), this.toIndex(p) + offset))
      .toArray();
    if (0 < commands.length) {
      this.do(new EditCommandSet(commands));
    }
  }

  private toIndex(page: TPageEditor): number {
    return Enumerable.from(this._pageEditors)
    .select((p, i) => ({ p, i }))
    .where(p => p.p.isReferedSameSource(page))
    .first()
    .i;
  }

  getPageCount(): number {
    return this.getOrDefault(s => s.pages.length, 0);
  }

  getCurrentPageNo(): number {
    return this._pageIndex + 1;
  }

  setCurrentPageNo(value: number): void {
    if ((value < 1) || (this.getPageCount() < value)) {
      return;
    }
    this._pageIndex = value - 1;
    // 現在ページとページ選択は別概念.連動させない.(連動させるならよくよく考える事.)
  }

  getCurrentScale(): number {
    return this._scale;
  }

  setCurrentScale(value: number): void {
    this._scale = value;
  }

  private clonePage(source: TPageEditor, update: (o: TPageEditor) => void): TPageEditor {
    const result = source.clone();
    update(result);
    return result;
  }

  selectPages(...pages: TPageEditor[]): void {
    const updated = Enumerable.from(pages)
      .select(p => this.clonePage(p, s => s.select()))
      .toArray();
    for (const u of updated) {
      this.updatePage(u);
    }
  }

  deselectPages(...pages: TPageEditor[]): void {
    const updated = Enumerable.from(pages)
      .select(p => this.clonePage(p, s => s.deselect()))
      .toArray();
    for (const u of updated) {
      this.updatePage(u);
    }
  }

  getSelectedPages(): TPageEditor[] {
    return Enumerable.from(this._pageEditors)
      .where(p => p.isSelected())
      .toArray();
  }

  selectAllPages(): void {
    this.selectPages(...Enumerable.from(this._pageEditors).where(p => !p.isSelected()).toArray());
  }

  deselectAllPages(): void {
    this.deselectPages(...Enumerable.from(this._pageEditors).where(p => p.isSelected()).toArray());
  }
}
